const DISALLOWED_OBJECT_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

export const normalize_text = (value) => String(value || '').trim().toLowerCase();

export const first_value = (values, fallback = '') => {
  const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== '');
  return value === undefined ? fallback : value;
};

export const first_nested_value = (record, paths = [], fallback = null) => {
  for (const path of paths) {
    const value = String(path)
      .split('.')
      .reduce((current, key) => (current && typeof current === 'object' ? Reflect.get(current, key) : undefined), record);

    if (value !== undefined && value !== null && value !== '') return value;
  }

  return fallback;
};

export const to_safe_object_key = (value) => {
  const normalized = String(value || '').trim();
  return normalized && !DISALLOWED_OBJECT_KEYS.has(normalized) ? normalized : '';
};

export const read_safe_object_value = (record, key, fallback = null) => {
  const safe_key = to_safe_object_key(key);
  if (!safe_key || !record || typeof record !== 'object') return fallback;
  return Object.prototype.hasOwnProperty.call(record, safe_key)
    ? Reflect.get(record, safe_key)
    : fallback;
};

export const write_safe_object_value = (record, key, value) => {
  const safe_key = to_safe_object_key(key);
  if (!safe_key || !record || typeof record !== 'object') return;
  Reflect.set(record, safe_key, value);
};

export const normalize_option = (option, index = 0) => {
  if (typeof option === 'string') return { id: `${index}`, label: option, value: option };

  const value = first_value([
    option?.value,
    option?.choice,
    option?.label,
    option?.name
  ], `option-${index + 1}`);

  return ({
    id: String(option?.id || value || index),
    label: first_value([option?.label, option?.choice, option?.name, value], `Option ${index + 1}`),
    value: String(value)
  });
};

export const resolve_question_type = (question) => first_value([
  question?.question_type?.question_type,
  question?.question_type?.type,
  question?.question_type,
  question?.type
], 'text');

export const resolve_question_options = (question) => first_value([
  Array.isArray(question?.multi_select_choices) ? question.multi_select_choices : null,
  Array.isArray(question?.options) ? question.options : null
], []);

export const normalize_question_type = (type) => {
  const normalized_type = normalize_text(type).replace(/[\s-]+/g, '_');

  if (['longtext', 'long_textbox', 'multi_line_text', 'multiline_text'].includes(normalized_type)) return 'long_text';
  if (['short_text', 'textbox', 'text_box', 'string'].includes(normalized_type)) return 'text';
  if (['single_choice', 'single_select', 'choice', 'choices', 'dropdown_list'].includes(normalized_type)) return 'single_select';
  if (['multiple_choice', 'multiple_select', 'multiselect', 'multi_choice'].includes(normalized_type)) return 'multi_select';
  if (['upload', 'document', 'attachment_upload'].includes(normalized_type)) return 'file';
  return normalized_type || 'text';
};

export const build_question_options = (type, options) => {
  if (options.length) return options;

  if (['yes_no', 'boolean'].includes(type)) {
    return [
      { id: 'yes', label: 'Yes', value: 'Yes' },
      { id: 'no', label: 'No', value: 'No' }
    ];
  }

  return options;
};

const resolve_question_answer = (question = {}) => (
  first_value([
    question?.answer,
    question?.latest_answer?.answer,
    Array.isArray(question?.answers) ? question.answers[0]?.answer : undefined
  ], null)
);

export const normalize_question = (
  question,
  index = 0,
  normalizeDescription = (value) => value,
  dependencyMeta = {}
) => {
  const question_type = resolve_question_type(question);
  const options = resolve_question_options(question)
    .map((option, option_index) => normalize_option(option, option_index));
  const normalized_type = normalize_question_type(question_type);
  const resolved_answer = resolve_question_answer(question);
  const dependent_questions = first_value([
    Array.isArray(question?.dependent_questions) ? question.dependent_questions : null,
    Array.isArray(question?.dependant_questions) ? question.dependant_questions : null
  ], []);
  const normalized_question = {
    id: Number(first_value([question?.section_question_id, question?.id], 0)) || null,
    section_question_id: Number(first_value([question?.section_question_id, question?.id], 0)) || null,
    // Risk assessments bind to the base Question, while form submissions use
    // the SectionQuestion id stored separately in `id`.
    question_id: Number(question?.question_id || question?.question?.id || question?.id || 0) || null,
    label: first_value([question?.question_text, question?.label], `Question ${index + 1}`),
    description: normalizeDescription(first_value([question?.question_description, question?.description], '')),
    type: normalized_type,
    is_mandatory: Boolean(question?.is_mandatory),
    options: build_question_options(normalized_type, options),
    trigger_condition: String(question?.trigger_condition ?? '').trim(),
    is_dependent_question: Boolean(dependencyMeta?.parent_question_id),
    dependency_depth: Number(dependencyMeta?.depth || 0),
    parent_question_id: dependencyMeta?.parent_question_id || null,
    parent_question_label: dependencyMeta?.parent_question_label || '',
    answer: normalized_type === 'single_select' && Array.isArray(resolved_answer)
      ? (resolved_answer[0] || '')
      : resolved_answer,
    dependent_questions: dependent_questions
      .map((dependent_question, question_index) => normalize_question(
        dependent_question,
        question_index,
        normalizeDescription,
        {
          parent_question_id: Number(first_value([question?.section_question_id, question?.id], 0)) || null,
          parent_question_label: first_value([question?.question_text, question?.label], `Question ${index + 1}`),
          depth: Number(dependencyMeta?.depth || 0) + 1
        }
      ))
  };

  return normalized_question;
};

export const normalize_sub_section = (sub_section, index = 0, normalizeDescription) => ({
  id: Number(first_value([sub_section?.section_id, sub_section?.id], index + 1)) || index + 1,
  label: first_value([sub_section?.section_name, sub_section?.name], `Section ${index + 1}`),
  access: sub_section?.access || null,
  instructions: (Array.isArray(sub_section?.form_instructions) ? sub_section.form_instructions : [])
    .map((instruction) => String(first_value([
      instruction?.content,
      instruction?.text,
      instruction?.description
    ], '')).trim())
    .filter(Boolean),
  questions: (Array.isArray(sub_section?.questions) ? sub_section.questions : [])
    .map((question, question_index) => normalize_question(question, question_index, normalizeDescription))
});

export const normalize_main_section = (main_section, index = 0, normalizeDescription = (value) => value) => ({
  id: Number(first_value([main_section?.main_section_id, main_section?.id], index + 1)) || index + 1,
  number: index + 1,
  label: first_value([main_section?.main_section_name, main_section?.name], `Step ${index + 1}`),
  description: first_value([main_section?.description], ''),
  sub_sections: [
    ...(Array.isArray(main_section?.questions) && main_section.questions.length
      ? [{
          id: `main-${index + 1}`,
          section_name: 'Details',
          questions: main_section.questions
        }]
      : []),
    ...(
      Array.isArray(main_section?.sub_sections)
        ? main_section.sub_sections
        : Array.isArray(main_section?.sections)
          ? main_section.sections
          : []
    )
  ].map((sub_section, sub_index) => normalize_sub_section(sub_section, sub_index, normalizeDescription))
});

export const normalize_saved_answer = (answer) => {
  const section_question_id = Number(first_value([
    answer?.section_question_id,
    answer?.question_id,
    answer?.id
  ], 0)) || null;

  if (!section_question_id) return null;

  const file_answer = answer?.answer && typeof answer.answer === 'object'
    ? answer.answer
    : null;
  const file_answers = Array.isArray(file_answer?.files)
    ? file_answer.files
    : Array.isArray(answer?.files)
      ? answer.files
      : [];
  const normalized_answer = {
    id: Number(first_value([answer?.answer_id, answer?.id], 0)) || null,
    section_question_id,
    question_label: String(first_value([
      answer?.question_text,
      answer?.label,
      answer?.question?.question_text,
      answer?.question?.label
    ], '')).trim().toLowerCase(),
    answer_text: first_value([
      file_answer?.file_name,
      answer?.answer_text,
      answer?.answer,
      answer?.value,
      answer?.response,
      answer?.display_value
    ], ''),
    file_url: first_value([
      answer?.presigned_url,
      file_answer?.presigned_url,
      answer?.file_url,
      answer?.url,
      file_answer?.original_url
    ], ''),
    file_name: first_value([
      file_answer?.file_name,
      answer?.file_name,
      answer?.document_name,
      answer?.name
    ], ''),
    answer_group_key: String(first_value([
      answer?.answer_group_key,
      answer?.group_key,
      answer?.instance_key
    ], '')).trim() || null,
    files: file_answers
  };

  return normalized_answer;
};

export const extract_nested_section_answers = (application_form) => {
  const sections = [
    ...(Array.isArray(application_form?.form?.sections) ? application_form.form.sections : []),
    ...(Array.isArray(application_form?.sections) ? application_form.sections : [])
  ];

  return sections.flatMap((section) => (
    Array.isArray(section?.questions)
      ? section.questions.map((question) => normalize_saved_answer(question)).filter(Boolean)
      : []
  ));
};

export const extract_saved_answers = (application_form) => {
  const answer_sources = [
    application_form?.answers,
    application_form?.application_form_answers,
    application_form?.form_answers,
    application_form?.application_answers,
    application_form?.question_answers,
    application_form?.submitted_answers,
    application_form?.responses,
    application_form?.data?.answers,
    application_form?.data?.application_form_answers,
    application_form?.data?.form_answers,
    application_form?.form?.answers
  ];

  const array_source = answer_sources.find((source) => Array.isArray(source));
  if (array_source) return array_source.map((answer) => normalize_saved_answer(answer)).filter(Boolean);

  const object_source = answer_sources.find((source) => source && typeof source === 'object');
  if (object_source) {
    return Object.entries(object_source)
      .map(([section_question_id, value]) => (
        normalize_saved_answer({
          section_question_id,
          ...(value && typeof value === 'object' ? value : { answer_text: value })
        })
      ))
      .filter(Boolean);
  }

  return extract_nested_section_answers(application_form);
};
